import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import { Shield, CreditCard, Lock, Loader2, Coins } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { SubscriptionModal } from "./SubscriptionModal";

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
  popular: boolean;
}

interface CreditPurchaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPurchaseComplete: () => void;
}

export const CreditPurchaseModal = ({ open, onOpenChange, onPurchaseComplete }: CreditPurchaseModalProps) => {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const { hasActiveSubscription, isLoading: subscriptionLoading } = useSubscription();
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (open && hasActiveSubscription) {
      fetchPackages();
    }
  }, [open, hasActiveSubscription]);

  const fetchPackages = async () => {
    try {
      const { data, error } = await supabase
        .from("credit_packages")
        .select("*")
        .eq("active", true)
        .order("price_cents");
      
      if (error) throw error;
      setPackages(data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast({
        title: t('purchase.error'),
        description: t('purchase.loadError'),
        variant: "destructive",
      });
    }
  };

  const handlePurchase = async (packageId: string) => {
    setLoading(true);
    setSelectedPackage(packageId);
    try {
      const { data, error } = await supabase.functions.invoke("purchase-credits", {
        body: { packageId },
      });

      if (error) throw error;

      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Error purchasing credits:", error);
      toast({
        title: t('purchase.error'),
        description: t('purchase.purchaseError'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // If loading subscription status
  if (subscriptionLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // If not subscribed, show subscription prompt
  if (!hasActiveSubscription) {
    return (
      <>
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center">
                {t("credits.subscriptionRequired", "Subscription Required")}
              </DialogTitle>
              <DialogDescription className="text-center">
                {t("credits.subscriptionRequiredDesc", "Subscribe to purchase additional credit packs")}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col items-center py-6 space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Lock className="h-8 w-8 text-primary" />
              </div>
              <p className="text-center text-sm text-muted-foreground">
                {t("credits.subscribeFirst", "Credit packs are available for subscribers only. Subscribe to a plan to unlock credit purchases.")}
              </p>
              <Button onClick={() => {
                onOpenChange(false);
                setShowSubscriptionModal(true);
              }}>
                {t("subscription.viewPlans", "View Plans")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <SubscriptionModal 
          open={showSubscriptionModal} 
          onOpenChange={setShowSubscriptionModal} 
        />
      </>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('purchase.title')}</DialogTitle>
          <DialogDescription>
            {t('purchase.subtitle', 'Add more credits to your account')}
          </DialogDescription>
        </DialogHeader>
        
        {/* Security messaging */}
        <div className="flex items-center justify-center gap-4 pb-4 border-b border-border">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Shield className="h-4 w-4 text-green-600" />
            <span className="text-sm">{t('purchase.securedByStripe')}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <CreditCard className="h-4 w-4 text-blue-600" />
            <span className="text-sm">{t('purchase.sslEncryption')}</span>
          </div>
        </div>
        
        <div className="space-y-3">
          {packages.map((pkg) => (
            <Card key={pkg.id} className="hover:border-primary/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Coins className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{pkg.credits.toLocaleString()} {t('purchase.credits', 'Credits')}</CardTitle>
                      <CardDescription>
                        ${(pkg.price_cents / 100 / pkg.credits * 100).toFixed(2)} {t('purchase.per100Credits', 'per 100 credits')}
                      </CardDescription>
                    </div>
                  </div>
                  <Button
                    onClick={() => handlePurchase(pkg.id)}
                    disabled={loading}
                    className="min-w-[80px]"
                  >
                    {loading && selectedPackage === pkg.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      formatPrice(pkg.price_cents)
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          {t('purchase.neverExpire', 'Credits never expire')}
        </p>
      </DialogContent>
    </Dialog>
  );
};
